import * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import type { Yard } from './Yard';

/**
 * Yard visitors. A squirrel bolts across to the tree; a cat walks the fence,
 * sits, and stares. The dog's reaction is the point: she drops whatever she
 * was doing (attention: visitor beats everything), chases the squirrel
 * barking and ends up under the tree looking up; she freezes and growls at
 * the cat and doesn't dare chase.
 */
export type VisitorKind = 'squirrel' | 'cat' | 'mail';
type Phase = 'none' | 'enter' | 'pause' | 'run' | 'climb' | 'sit' | 'leave';

const _v = new THREE.Vector3();

export class Visitors {
  onMessage?: (text: string) => void;
  onChased?: (kind: VisitorKind) => void;
  /** the squirrel race: did she catch up before it reached the tree? */
  onRace?: (won: boolean) => void;
  private raceDone = false;
  /** called when a visitor appears; fetch/feeding etc. should yield */
  onInterrupt?: () => void;
  /** minimum seconds between visitors */
  minGap = 45;
  maxGap = 130;

  private kind: VisitorKind | null = null;
  private phase: Phase = 'none';
  private mesh = new THREE.Group();
  private squirrel: THREE.Group;
  private cat: THREE.Group;
  private carrier: THREE.Group;
  private t = 0;
  private phaseT = 0;
  private nextAt = 25;
  private from = new THREE.Vector3();
  private to = new THREE.Vector3();
  private speed = 3;
  private dogChasing = false;
  private lastBark = 0;
  private squeakedAt = 0;

  constructor(
    scene: THREE.Object3D,
    private yard: Yard,
    private dog: Dog,
  ) {
    // squirrel: small body, big tail curled up over the back
    const sq = (this.squirrel = new THREE.Group());
    const fur = new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.9 });
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.05, 0.12, 4, 8), fur);
    body.rotation.x = Math.PI / 2;
    body.position.y = 0.07;
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.045, 10, 8), fur);
    head.position.set(0, 0.11, 0.1);
    const tail = new THREE.Mesh(new THREE.CapsuleGeometry(0.04, 0.16, 4, 8), fur);
    tail.position.set(0, 0.16, -0.1);
    tail.rotation.x = -0.4;
    for (const m of [body, head, tail]) m.castShadow = true;
    sq.add(body, head, tail);

    // cat: grey, sitting silhouette
    const ct = (this.cat = new THREE.Group());
    const grey = new THREE.MeshStandardMaterial({ color: 0x777069, roughness: 0.85 });
    const cbody = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.14, 4, 10), grey);
    cbody.position.y = 0.16;
    cbody.rotation.x = 0.35;
    const chead = new THREE.Mesh(new THREE.SphereGeometry(0.075, 12, 10), grey);
    chead.position.set(0, 0.34, 0.06);
    const earGeo = new THREE.ConeGeometry(0.025, 0.05, 6);
    for (const s of [-1, 1]) {
      const ear = new THREE.Mesh(earGeo, grey);
      ear.position.set(0.045 * s, 0.41, 0.05);
      ct.add(ear);
    }
    const eyes = new THREE.MeshStandardMaterial({ color: 0xc8e04a, emissive: 0x4a5a10, roughness: 0.3 });
    for (const s of [-1, 1]) {
      const e = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 6), eyes);
      e.position.set(0.03 * s, 0.35, 0.125);
      ct.add(e);
    }
    const ctail = new THREE.Mesh(new THREE.CapsuleGeometry(0.02, 0.22, 4, 8), grey);
    ctail.position.set(0.08, 0.1, -0.1);
    ctail.rotation.z = 0.5;
    for (const m of [cbody, chead, ctail]) m.castShadow = true;
    ct.add(cbody, chead, ctail);

    // mail carrier: blue uniform, cap, satchel — stands outside the gate for a bit
    const mc = (this.carrier = new THREE.Group());
    const blue = new THREE.MeshStandardMaterial({ color: 0x3b5b9a, roughness: 0.9 });
    const skin = new THREE.MeshStandardMaterial({ color: 0xd9a77a, roughness: 0.9 });
    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.16, 0.5, 4, 10), blue);
    torso.position.y = 0.95;
    const mhead = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 10), skin);
    mhead.position.y = 1.42;
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.06, 12), blue);
    cap.position.y = 1.52;
    const legs = new THREE.Mesh(new THREE.CapsuleGeometry(0.14, 0.5, 4, 10), new THREE.MeshStandardMaterial({ color: 0x2a3a5a, roughness: 1 }));
    legs.position.y = 0.42;
    const bag = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.26, 0.1), new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 1 }));
    bag.position.set(0.24, 0.85, 0);
    for (const m of [torso, mhead, cap, legs, bag]) m.castShadow = true;
    mc.add(torso, mhead, cap, legs, bag);

    this.mesh.add(sq, ct, mc);
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  get active(): VisitorKind | null {
    return this.kind;
  }

  /** world position of the current visitor (for toy hits) */
  get position(): THREE.Vector3 | null {
    return this.kind ? this.mesh.position : null;
  }

  /** the toy got them: they leave in a huff */
  hitByToy(): boolean {
    if (!this.kind || this.phase === 'leave' || this.phase === 'climb' || this.phase === 'none') return false;
    if (this.kind === 'squirrel') {
      this.to.copy(this.yard.tree.position).setY(0);
      this.speed = 6;
      this.face(this.to);
      this.phase = 'run';
      this.dog.sounds.play('squirrel', 0.8);
    } else if (this.kind === 'cat') {
      this.to.copy(this.yard.fenceB);
      this.face(this.to);
      this.speed = 2.6;
      this.phase = 'leave';
      this.dog.sounds.play('cat', 0.9);
    } else {
      this.to.copy(this.yard.fenceB).setY(0).add(_v.subVectors(this.yard.gateOutside, this.yard.gateInside).setLength(1.2));
      this.face(this.to);
      this.speed = 2.2;
      this.phase = 'leave';
    }
    this.onMessage?.(this.kind === 'mail' ? '"hey!" — the mail carrier moves along' : this.kind === 'cat' ? 'the cat yowls and bolts' : 'the squirrel scarpers');
    return true;
  }

  /** force one (debug / testing) */
  trigger(kind: VisitorKind): void {
    this.raceDone = false;
    if (this.kind) return;
    this.kind = kind;
    this.mesh.visible = true;
    this.squirrel.visible = kind === 'squirrel';
    this.cat.visible = kind === 'cat';
    this.carrier.visible = kind === 'mail';
    this.phaseT = 0;
    this.dogChasing = false;
    this.onInterrupt?.();
    if (kind === 'squirrel') {
      // enter from the left edge, pause partway, then run for the tree
      this.from.set(-8, 0, -1 + Math.random() * 2);
      this.to.copy(this.yard.tree.position).setY(0);
      _v.subVectors(this.to, this.from);
      this.to.copy(this.from).addScaledVector(_v, 0.45); // pause point
      this.mesh.position.copy(this.from);
      this.speed = 2.6;
      this.phase = 'enter';
      this.dog.sounds.play('squirrel', 0.6);
      this.onMessage?.('squirrel!');
    } else if (kind === 'mail') {
      // along the outside of the fence to the gate, stand a while, carry on
      this.from.copy(this.yard.fenceA).setY(0).add(_v.subVectors(this.yard.gateOutside, this.yard.gateInside).setLength(1.2));
      this.to.copy(this.yard.gateOutside);
      this.mesh.position.copy(this.from);
      this.speed = 1.1;
      this.phase = 'enter';
      this.onMessage?.('the mail!');
    } else {
      // walk the fence from one end to the middle, sit, stare, leave
      this.from.copy(this.yard.fenceA);
      this.to.lerpVectors(this.yard.fenceA, this.yard.fenceB, 0.5);
      this.mesh.position.copy(this.from);
      this.speed = 0.9;
      this.phase = 'enter';
      this.onMessage?.('a cat on the fence');
    }
    this.face(this.to);
  }

  private face(p: THREE.Vector3): void {
    _v.subVectors(p, this.mesh.position).setY(0);
    if (_v.lengthSq() > 1e-6) this.mesh.rotation.y = Math.atan2(_v.x, _v.z);
  }

  /** move toward `to`; returns true on arrival */
  private step(dt: number, hop: boolean): boolean {
    _v.subVectors(this.to, this.mesh.position);
    const d = _v.length();
    if (d < this.speed * dt) {
      this.mesh.position.copy(this.to);
      return true;
    }
    this.mesh.position.addScaledVector(_v.normalize(), this.speed * dt);
    if (hop) this.squirrel.position.y = Math.abs(Math.sin(this.t * 14)) * 0.08;
    return false;
  }

  private end(): void {
    this.kind = null;
    this.phase = 'none';
    this.mesh.visible = false;
    this.squirrel.position.y = 0;
    this.dog.release();
    this.nextAt = this.t + this.minGap + Math.random() * (this.maxGap - this.minGap);
  }

  update(dt: number, you: THREE.Vector3): void {
    this.t += dt;
    if (!this.kind) {
      if (this.t > this.nextAt && document.visibilityState === 'visible') {
        const r = Math.random();
        this.trigger(r < 0.5 ? 'squirrel' : r < 0.75 ? 'cat' : 'mail');
      }
      return;
    }
    this.phaseT += dt;
    const dog = this.dog;
    const dogDist = dog.root.position.distanceTo(this.mesh.position);

    if (this.kind === 'mail') {
      switch (this.phase) {
        case 'enter':
          if (this.step(dt, false)) {
            this.phase = 'sit';
            this.phaseT = 0;
            this.face(dog.root.position);
          }
          break;
        case 'sit':
          if (this.phaseT > 6 + Math.random() * 4) {
            this.to.copy(this.yard.fenceB).setY(0).add(_v.subVectors(this.yard.gateOutside, this.yard.gateInside).setLength(1.2));
            this.face(this.to);
            this.speed = 1.2;
            this.phase = 'leave';
          }
          break;
        case 'leave':
          if (this.step(dt, false)) this.end();
          break;
        default:
          break;
      }
      // dog side: to the gate, and bark at them the whole time
      if (!this.dogChasing && this.phaseT > 0.4) {
        this.dogChasing = true;
        dog.engage(this.mesh.position, 'chase');
        this.onChased?.('mail');
      }
      if (this.dogChasing) {
        dog.chase(this.yard.gateInside, 0.15);
        dog.lookAt(this.mesh.position);
        if (this.t - this.lastBark > 0.7 + Math.random() * 0.9) {
          dog.barkNow(Math.random() < 0.4 ? 2 : 1);
          this.lastBark = this.t;
        }
      }
      return;
    }

    if (this.kind === 'squirrel') {
      // squirrel side
      switch (this.phase) {
        case 'enter':
          if (this.step(dt, true)) {
            this.phase = 'pause';
            this.phaseT = 0;
            this.squirrel.position.y = 0;
          }
          break;
        case 'pause':
          // sits up; bolts early if the dog closes in
          if (this.phaseT > 0.9 + Math.random() * 0.5 || dogDist < 2.2) {
            this.to.copy(this.yard.tree.position).setY(0);
            this.speed = dogDist < 2.2 ? 5.5 : 3.5;
            this.face(this.to);
            this.phase = 'run';
            if (this.t - this.squeakedAt > 2) {
              this.dog.sounds.play('squirrel', 0.7);
              this.squeakedAt = this.t;
            }
          }
          break;
        case 'run':
          if (dogDist < 2.5) this.speed = 5.5;
          if (!this.raceDone && dogDist < 0.75) {
            // caught up before the tree: the squirrel still gets away, but that counts
            this.raceDone = true;
            this.onRace?.(true);
          }
          if (this.step(dt, true)) {
            if (!this.raceDone) {
              this.raceDone = true;
              this.onRace?.(false);
            }
            this.phase = 'climb';
            this.phaseT = 0;
            this.squirrel.position.y = 0;
          }
          break;
        case 'climb':
          // straight up the trunk and into the leaves
          this.mesh.position.y = Math.min(2.4, this.phaseT * 2.2);
          if (this.mesh.position.y >= 2.4) {
            this.phase = 'leave';
            this.phaseT = 0;
          }
          break;
        case 'leave':
          this.mesh.visible = this.phaseT < 0.5;
          if (this.phaseT > 4) this.end();
          break;
        default:
          break;
      }
      // dog side: notice, then chase with barks; under the tree, look up
      if (!this.dogChasing && this.phaseT > 0.35) {
        this.dogChasing = true;
        dog.engage(this.mesh.position, 'chase');
        this.onChased?.('squirrel');
      }
      if (this.dogChasing) {
        if (this.phase === 'enter' || this.phase === 'pause' || this.phase === 'run') {
          _v.copy(this.mesh.position).setY(0);
          dog.chase(_v);
          if (this.t - this.lastBark > 0.9 + Math.random() * 0.8 && dogDist < 5) {
            dog.barkNow(1);
            this.lastBark = this.t;
          }
        } else {
          // gone up the tree: stop at the trunk, look up, a couple of barks
          _v.copy(this.yard.tree.position).setY(0);
          dog.chase(_v, 0.9);
          dog.lookAt(this.yard.treeTop);
          if (this.t - this.lastBark > 1.6 && this.phaseT < 3) {
            dog.barkNow(2);
            this.lastBark = this.t;
          }
        }
      }
    } else {
      // cat side
      switch (this.phase) {
        case 'enter':
          if (this.step(dt, false)) {
            this.phase = 'sit';
            this.phaseT = 0;
            this.face(dog.root.position);
            this.dog.sounds.play('cat', 0.5);
          }
          break;
        case 'sit':
          // stares at the dog; leaves after a while, sooner if the dog comes close
          this.face(dog.root.position);
          if (this.phaseT > 9 + Math.random() * 6 || dogDist < 1.6) {
            this.to.copy(this.yard.fenceB);
            this.face(this.to);
            this.speed = 1.6;
            this.phase = 'leave';
          }
          break;
        case 'leave':
          if (this.step(dt, false)) this.end();
          break;
        default:
          break;
      }
      // dog side: freeze and growl, ears/eyes locked; a bark once it's gone
      if (!this.dogChasing && this.phaseT > 0.5) {
        this.dogChasing = true;
        dog.engage(this.mesh.position, 'freeze');
        this.onChased?.('cat');
      }
      if (this.dogChasing) {
        dog.lookAt(this.mesh.position);
        if (this.phase === 'sit' && this.t - this.lastBark > 3 + Math.random() * 3) {
          dog.sounds.play('growl', 0.8);
          this.lastBark = this.t;
        }
      }
    }
    void you;
  }
}
